import { Box, Grid, makeStyles, PlaceIcon, Theme, Typography } from '@freighthub/silhouette-core'
import { Address, isAddress, isPort, Port } from 'features/Directory'
import { ShipmentMode } from 'features/ShipmentDetails'
import { PdadStepIcon, TransportModeIcon } from 'features/TransportPlan'
import { includes } from 'lodash-es'
import { FC, PropsWithChildren, ReactElement } from 'react'
import { formatMomentShort } from 'utils/date'
import { useI18n } from 'utils/localisation'

import { msToDays } from '../formatDate'
import { QuoteOptionDuration } from '../QuoteSheet/QuoteOption/QuoteOptionDuration'
import { IQuoteSheet } from '../QuoteSheet/QuoteSheetModel'
import { arrivalDate, IQuoteOption } from '../QuoteSheet/QuoteSheetOption'

const destinationModeTerms = {
  air: 'booking-transport_plan-arrival_airport',
  sea: 'booking-transport_plan-pod',
  rail: 'booking-transport_plan-arrival_rail',
  seaAir: 'booking-transport_plan-arrival_airport',
}

const departureModeTerms = {
  air: 'booking-transport_plan-departure_airport',
  sea: 'booking-transport_plan-pol',
  rail: 'booking-transport_plan-departure_rail',
  seaAir: 'booking-transport_plan-departure_airport',
}

const useStyles = makeStyles<Theme>((theme) => ({
  container: {
    textAlign: 'center',
    marginLeft: theme.spacing(-1),
  },
  label: {
    display: 'block',
    fontSize: theme.typography.caption.fontSize,
    margin: theme.spacing(2, 0, 1),
    fontWeight: theme.typography.fontWeightBold,
  },
  item: {
    flex: 1,
  },
}))

/**
 * One column including required header icon
 */
interface ColumnProps {
  icon: ReactElement
}

const Column: FC<PropsWithChildren<ColumnProps>> = ({ icon, children }) => {
  const styles = useStyles()
  return (
    <Grid spacing={1} className={styles.item}>
      {icon}
      {children}
    </Grid>
  )
}

/**
 * One row including required headline
 */
interface RowProps {
  headline: string
}
const Row: FC<PropsWithChildren<RowProps>> = ({ headline, children }) => {
  const styles = useStyles()
  return (
    <Box>
      <Typography variant="caption" className={styles.label}>
        {headline}
      </Typography>
      {children}
    </Box>
  )
}

interface PortProps {
  port: Port
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
  time?: any
  mode: ShipmentMode
  isDestination: boolean
}

const getIcon = (mode: string, isDestination: boolean) => {
  if (mode === 'air' || mode === 'seaAir') {
    return 'airportTower'
  }
  return isDestination ? 'pod' : 'pol'
}

const PortIcon: FC<PortProps> = ({ port, time, mode, isDestination }) => {
  if (!port) {
    return null
  }
  const { translate } = useI18n()
  return (
    <Column icon={<PdadStepIcon iconType={getIcon(mode, isDestination)} />}>
      {time && (
        <Row
          headline={
            isDestination ? translate('booking-transport_plan-discharge') : translate('booking-transport_plan-etd')
          }
        >
          <Typography>{formatMomentShort(time)}</Typography>
        </Row>
      )}
      <Row headline={isDestination ? translate(destinationModeTerms[mode]) : translate(departureModeTerms[mode])}>
        <Typography>
          {port._id} - {port.name}
        </Typography>
      </Row>
    </Column>
  )
}

/**
 * Address Layout
 */
interface AddressProps {
  address: Address
}

const AddressComponent: FC<AddressProps> = ({ address }) => (
  <div>
    <Typography>{address.companyName || ''}</Typography>
    <Typography>{`${address.street || ''} ${address.streetNumber || ''}`}</Typography>
    <Typography>{`${address.postalCode || ''} ${address.city || ''}`}</Typography>
    <Typography>{address.state || ''}</Typography>
    <Typography>{address.country}</Typography>
  </div>
)

/**
 * Transport Plan on top of booking page
 * Shows the individual checkpoints of a shipment in a column based layout
 */

interface TransportPlanProps {
  option: IQuoteOption
  sheet: IQuoteSheet
}

export const TransportPlan: FC<TransportPlanProps> = ({ option, sheet }) => {
  const { preQuote, mainQuote, postQuote } = option.routeQuotes

  const preRoute = preQuote && preQuote.route
  const mainRoute = mainQuote && mainQuote.route
  const postRoute = postQuote && postQuote.route

  const mainJourney = mainRoute && mainRoute.journey

  const preRunDistance = preRoute && preRoute.distance
  const postRunDistance = postRoute && postRoute.distance

  // @ts-expect-error TS(2345) FIXME: Argument of type 'number | undefined' is not assig... Remove this comment to see the full error message
  const sailingDuration = msToDays(mainQuote.route.duration)

  const { routeSchedule } = mainQuote.route
  const vesselNames: string[] = routeSchedule
    ? routeSchedule.oceanLegs
        .filter(({ vessel: { name } }) => !includes(['BARGE', 'TRUCK', 'RAIL', 'FEEDER'], name))
        .map(({ vessel }) => vessel.name)
    : []

  const styles = useStyles()
  const { translate } = useI18n()
  return (
    <Grid container className={styles.container}>
      {/* CRD */}
      {sheet.request.cargoReadyAt && (
        <Column icon={<PdadStepIcon iconType="pallets" />}>
          <Row headline={translate('booking-transport_plan-cargo_ready')}>
            <Typography>{formatMomentShort(sheet.request.cargoReadyAt)}</Typography>
          </Row>
        </Column>
      )}

      {/* PRE */}
      {isAddress(sheet.request.journey.origin) && (
        <Column icon={<PdadStepIcon iconType="warehouse" />}>
          <Row headline={translate('booking-transport_plan-origin')}>
            <AddressComponent address={sheet.request.journey.origin as Address} />
          </Row>
        </Column>
      )}

      {isAddress(sheet.request.journey.origin) && preQuote && (
        <Column
          icon={
            <TransportModeIcon
              data-tracking-id={`booking-pre_carriage-transport_mode-${preQuote.mode}`}
              transportMode={preQuote.mode}
            />
          }
        >
          <Row headline={translate('booking-transport_plan-pre_carriage')}>
            {preRunDistance && (
              <Typography>
                {Math.round(preRunDistance / 1000)}
                km
              </Typography>
            )}
            <Box>
              <QuoteOptionDuration approximate option={option} sheet={sheet} routeQuoteType="preQuote" />
            </Box>
          </Row>
        </Column>
      )}

      {/* MAIN */}
      {isPort(mainJourney && mainJourney.origin) && (
        <PortIcon
          isDestination={false}
          time={mainRoute.etd}
          mode={mainQuote.mode as ShipmentMode}
          port={mainJourney.origin as Port}
        />
      )}

      <Column
        icon={
          <TransportModeIcon
            data-tracking-id={`booking-main-transport_mode-${mainQuote.mode}`}
            transportMode={mainQuote.mode}
            data-tracking-tp-mode={mainQuote.mode}
          />
        }
      >
        {vesselNames.length > 0 && (
          <Row headline={translate('booking-transport_plan-vessel')}>
            {vesselNames.map((vesselName, idx) => (
              // eslint-disable-next-line react/no-array-index-key
              <Typography key={idx}>{vesselName}</Typography>
            ))}
          </Row>
        )}

        {option.type === 'lcl' && (
          <Row headline={translate('booking-transport_plan-duration')}>
            <QuoteOptionDuration approximate option={option} sheet={sheet} routeQuoteType="mainQuote" />
          </Row>
        )}
        {option.type === 'fcl' && (
          <Row headline={translate('booking-transport_plan-sailing_duration')}>
            <Typography>
              {sailingDuration} {translate('day', { count: sailingDuration })}
            </Typography>
          </Row>
        )}
      </Column>

      {isPort(mainJourney && mainJourney.destination) && (
        <PortIcon
          isDestination
          time={mainRoute.eta}
          mode={mainQuote.mode as ShipmentMode}
          port={mainJourney.destination as Port}
        />
      )}

      {/* POST */}
      {isAddress(sheet.request.journey.destination) && postQuote && (
        <Column
          icon={
            <TransportModeIcon
              data-tracking-id={`booking-on_carriage-transport_mode-${postQuote.mode}`}
              transportMode={postQuote.mode}
            />
          }
        >
          <Row headline={translate('booking-transport_plan-on_carriage')}>
            {postRunDistance && (
              <Typography>
                {Math.round(postRunDistance / 1000)}
                km
              </Typography>
            )}
            <div>
              <QuoteOptionDuration approximate option={option} sheet={sheet} routeQuoteType="postQuote" />
            </div>
          </Row>
        </Column>
      )}

      {isAddress(sheet.request.journey.destination) && (
        <Column icon={<PlaceIcon />}>
          {option.type === 'fcl' && option.routeQuotes.mainQuote.mode === 'sea' && (
            <Row headline={translate('booking-transport_plan-delivery')}>
              <Typography>{formatMomentShort(arrivalDate(option))}</Typography>
            </Row>
          )}
          <Row headline={translate('booking-transport_plan-destination')}>
            <AddressComponent address={sheet.request.journey.destination as Address} />
          </Row>
        </Column>
      )}
    </Grid>
  )
}
